import classNames from 'classnames';
import React from 'react';
import { get } from 'lodash';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiX } from 'react-icons/fi';

import { useWorkspace, useActions } from '@mssgme/hooks';
import {
    SidebarHeader,
    ScrollableBox,
    Button,
    Notification,
    EnhancedNavigationGuard,
    FormikProvider,
    Form,
    useEnhancedFormik,
    CancelButton,
    SaveButton,
    Feature,
} from '@mssgme/ui';

import { useMessengerHelpHint } from '../../../hooks';
import { PageLayout } from '../../../UI';
import { LABELS, getPlaceholder } from './helpers';
import NameField from './NameField';

import styles from './MessengerEdit.scss';

export default function MessengerEdit() {
    const history = useHistory();
    const actions = useActions();
    const { t } = useTranslation('cards');
    const { card } = useWorkspace();
    const { platform } = useParams();

    const messenger = card.messengers.find((item) => item.messenger === platform);
    const isNew = !messenger;

    const formik = useEnhancedFormik({
        initialValues: {
            type: get(messenger, 'type', 'private'),
            name: get(messenger, 'name', ''),
            messenger: platform,
            enabled: true,
        },
        onSubmit: async (payload) => {
            isNew
                ? await actions.api.card.messengers.create({ payload })
                : await actions.api.card.messengers.update({ messengerId: messenger._id, payload });
            onSuccess('CardEditor.saved');
        },
    });
    const help = useMessengerHelpHint(formik.values);

    async function handleDelete(e) {
        e.preventDefault();
        await actions.api.card.messengers.delete({ messengerId: messenger._id });
        onSuccess('CardEditor.removed');
    }

    function onSuccess(message) {
        setTimeout(() => {
            Notification.notify(t(message), { showClose: true });

            history.push('/p/edit/messengers');
        }, 10);
    }

    const placeholder = getPlaceholder(formik.values, t);
    const label = LABELS[platform];

    const leftButton = <CancelButton tag={Link} to="/p/edit/messengers" styled="backIcon" />;

    return (
        <FormikProvider value={formik}>
            <EnhancedNavigationGuard>
                <PageLayout>
                    <SidebarHeader heading={label} leftButton={leftButton} rightButton={<SaveButton />} />
                    <ScrollableBox>
                        <Feature rule="messengers:edit" subject={formik.values}>
                            <Form className={classNames({ [styles.formNew]: !isNew })}>
                                <NameField name="name" label={label} placeholder={placeholder} autoFocus />

                                {!isNew && (
                                    <Button
                                        wrapContentStyle={{ overflow: 'visible' }}
                                        btnStyle="danger"
                                        size="small"
                                        type="button"
                                        onClick={handleDelete}
                                    >
                                        <FiX />
                                    </Button>
                                )}

                                {help && <div className={styles.help}>{help}</div>}
                            </Form>
                        </Feature>
                    </ScrollableBox>
                </PageLayout>
            </EnhancedNavigationGuard>
        </FormikProvider>
    );
}
